import {useUrlParams} from 'services/hooks'
import TablePaginationRaw from '@mui/material/TablePagination'
import {PAGE_SIZES} from 'constants/tables'
import {useCallback, useState, useEffect, useRef} from 'react'
import {useIsRtl} from 'store/Store'

const DELAY = 1000 // 1000ms delay — debounces rows-per-page only, not page navigation (see below)

const TablePagination = ({
    itemName,
    paginationType, // 'local', 'external' (server) or null
    totalItems,
    hasMore,
    rowsOnPage,
    isLoading,
}) => {
    const isRtl = useIsRtl()
    const {urlParams, setUrlParam} = useUrlParams()
    const {[`${itemName}_page`]: urlPage, [`${itemName}_pageSize`]: urlPageSize} = urlParams

    // Local UI state for immediate feedback
    const [localPage, setLocalPage] = useState(Number(urlPage || 0))
    const [localPageSize, setLocalPageSize] = useState(Number(urlPageSize || PAGE_SIZES[2]))

    // Ref for tracking the rows-per-page debounce timeout
    const pageSizeTimeoutRef = useRef(null)

    // Keep local state in sync with URL
    useEffect(() => {
        setLocalPage(Number(urlPage || 0))
    }, [urlPage])

    useEffect(() => {
        setLocalPageSize(Number(urlPageSize || PAGE_SIZES[2]))
    }, [urlPageSize])



    // Applied immediately, not debounced: with hasMore-driven pagination, "next" only means
    // "a page past the one we've already loaded exists". Debouncing this let a user click Next
    // several times before any request fired, then jump straight past the real last page once
    // the single coalesced request finally landed. Disabling the buttons while isLoading is what
    // now limits request volume, and it does so without letting the page run ahead of real data.
    const handlePageChange = useCallback((_, p) => {
        setLocalPage(p)
        setUrlParam(`${itemName}_page`, p)
    }, [itemName, setUrlParam])

    const handleRowsPerPageChange = useCallback((e) => {
        const newSize = parseInt(e.target.value, 10)
        setLocalPageSize(newSize)  // Update UI immediately
        setLocalPage(0)  // Reset to first page
        
        // Clear any pending timeout
        if (pageSizeTimeoutRef.current) {
            clearTimeout(pageSizeTimeoutRef.current)
        }

        // Set new timeout for URL updates
        pageSizeTimeoutRef.current = setTimeout(() => {
            setUrlParam(`${itemName}_pageSize`, newSize)
            setUrlParam(`${itemName}_page`, 0)
            pageSizeTimeoutRef.current = null
        }, DELAY)
    }, [itemName, setUrlParam])
    
    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (pageSizeTimeoutRef.current) {
                clearTimeout(pageSizeTimeoutRef.current)
            }
        }
    }, [])

    if (!paginationType) {
        return null
    }

    // The server may skip the count and only tell us whether a next page exists (hasMore).
    const unknownTotal = totalItems == null
    const firstRow = localPage * localPageSize + 1
    const labelDisplayedRows = () =>
        rowsOnPage ? `${firstRow}–${firstRow + rowsOnPage - 1}` : '0'

    // MUI's TablePaginationActions always binds the physically-third button to "go to the next
    // page" (increment), but under RTL it swaps which of slotProps.nextButton/previousButton it
    // applies to that button — see its source. So the slot we must set flips with direction, or
    // the increment button silently keeps its default (never disabled when count is unknown).
    const nextPageButtonSlot = isRtl ? 'previousButton' : 'nextButton'

    return (
        <TablePaginationRaw
            rowsPerPageOptions={PAGE_SIZES}
            component="div"
            count={unknownTotal ? -1 : totalItems}
            disabled={isLoading}
            labelDisplayedRows={unknownTotal ? labelDisplayedRows : undefined}
            slotProps={
                // A slotProps override replaces the button's disabled value entirely (it doesn't
                // OR with the top-level `disabled` prop above), so isLoading has to be folded in
                // here explicitly or a click mid-request could still fire.
                unknownTotal
                    ? {actions: {[nextPageButtonSlot]: {disabled: isLoading || !hasMore}}}
                    : undefined
            }
            rowsPerPage={localPageSize}
            page={localPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
        />
    )
}

export default TablePagination
